/**
 * Magazine (.mag) export utilities.
 *
 * Triggers browser download for magazines returned by POST /files/export.
 * Primary path is a streamed Blob (stream:true) ; base64 string remains as a
 * legacy fallback until the b64_mag branch is removed.
 */

/**
 * Triggers a browser download for a magazine payload.
 *
 * Accepts a Blob (from streamed `/files/export` with stream:true) or a base64
 * string (legacy b64_mag JSON path).
 */
export function downloadMagazine(payload: Blob | string, filename: string): void {
	let blob: Blob;
	if (typeof payload === 'string') {
		const byteString = atob(payload);
		const bytes = new Uint8Array(byteString.length);
		for (let i = 0; i < byteString.length; i += 1) {
			bytes[i] = byteString.charCodeAt(i);
		}
		blob = new Blob([bytes], { type: 'application/octet-stream' });
	} else {
		blob = payload;
	}

	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();

	setTimeout(() => {
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	}, 100);
}

/**
 * Generates a timestamped filename for magazine exports.
 *
 * When `opts` is provided, the filename encodes collection name and/or item
 * count for richer context, e.g. `MyCollection_5-items_2025-03-16.mag`.
 */
export function generateMagazineFilename(
	prefix?: string,
	opts?: { collectionName?: string; count?: number },
): string {
	const date = new Date().toISOString().slice(0, 10);
	const safePrefix = prefix || 'export';

	if (opts?.collectionName) {
		const sanitized = opts.collectionName
			.replace(/[^a-zA-Z0-9_-]/g, '_')
			.slice(0, 40);
		const countPart = opts.count != null ? `_${opts.count}-items` : '';
		return `${sanitized}${countPart}_${date}.mag`;
	}

	if (opts?.count != null) {
		return `${safePrefix}_${opts.count}-items_${date}.mag`;
	}

	return `${safePrefix}_${date}.mag`;
}
